import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getCategoryStats } from '../api';
import { getLocalizedCategoryTitle } from '../utils/categoryLabels';
import Icon from './Icon';
import '../css/admin.css';

const CHART_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#14b8a6',
  '#3b82f6',
  '#a855f7',
  '#ef4444',
  '#22c55e',
  '#eab308',
];

const NO_SLICE_COLOR = '#cbd5e1';

/** Доля внутреннего «дыра» от внешнего радиуса (inset 18% с каждой стороны кольца). */
const DONUT_INNER_RADIUS_RATIO = 0.64;

const TOOLTIP_W = 240;
const TOOLTIP_H = 64;
const TOOLTIP_PAD = 12;

const CategoryStats = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donutHover, setDonutHover] = useState(null);
  const donutWrapRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCategoryStats();
        setStats(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Ошибка загрузки статистики категорий:', err);
        setError(err.message || i18n.t('admin.categoryStats.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const totalCompanies = useMemo(
    () => stats.reduce((sum, cat) => sum + (Number(cat.companiesCount) || 0), 0),
    [stats]
  );

  const sortedByCount = useMemo(
    () =>
      [...stats].sort(
        (a, b) => (Number(b.companiesCount) || 0) - (Number(a.companiesCount) || 0)
      ),
    [stats]
  );

  const colorByCategoryId = useMemo(() => {
    const map = new Map();
    if (totalCompanies <= 0) return map;
    const positive = sortedByCount.filter((s) => (Number(s.companiesCount) || 0) > 0);
    positive.forEach((s, i) => {
      map.set(String(s.categoryId ?? ''), CHART_COLORS[i % CHART_COLORS.length]);
    });
    return map;
  }, [sortedByCount, totalCompanies]);

  const { conicGradient, sliceRanges } = useMemo(() => {
    if (totalCompanies <= 0) {
      return { conicGradient: null, sliceRanges: [] };
    }
    const positive = sortedByCount.filter((s) => (Number(s.companiesCount) || 0) > 0);
    if (positive.length === 0) {
      return { conicGradient: null, sliceRanges: [] };
    }
    let cursor = 0;
    const stops = [];
    const ranges = [];
    positive.forEach((s, i) => {
      const pct = ((Number(s.companiesCount) || 0) / totalCompanies) * 100;
      const color = CHART_COLORS[i % CHART_COLORS.length];
      const isLast = i === positive.length - 1;
      const next = isLast ? 100 : cursor + pct;
      stops.push(`${color} ${cursor}% ${next}%`);
      ranges.push({ category: s, start: cursor, end: next, isLast });
      cursor = next;
    });
    return {
      conicGradient: `conic-gradient(from -90deg, ${stops.join(', ')})`,
      sliceRanges: ranges,
    };
  }, [sortedByCount, totalCompanies]);

  const pickSliceByPercent = useCallback(
    (pIn) => {
      if (!sliceRanges.length) return null;
      const p = Math.min(100, Math.max(0, pIn));
      const eps = 1e-6;
      for (let i = 0; i < sliceRanges.length; i++) {
        const { start, end, isLast } = sliceRanges[i];
        if (p >= start - eps && (isLast ? p <= end + eps : p < end - eps)) {
          return sliceRanges[i].category;
        }
      }
      return sliceRanges[sliceRanges.length - 1].category;
    },
    [sliceRanges]
  );

  const updateDonutHover = useCallback(
    (e) => {
      const el = donutWrapRef.current;
      if (!el || !sliceRanges.length) {
        setDonutHover(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const dx = e.clientX - rect.left - cx;
      const dy = e.clientY - rect.top - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      const rOuter = size / 2;
      const rInner = rOuter * DONUT_INNER_RADIUS_RATIO;
      if (r < rInner || r > rOuter) {
        setDonutHover(null);
        return;
      }
      const theta = (Math.atan2(dx, -dy) * 180) / Math.PI;
      const thetaNorm = ((theta % 360) + 360) % 360;
      const p = (((thetaNorm - 270 + 360) % 360) / 360) * 100;
      const cat = pickSliceByPercent(p);
      if (!cat) {
        setDonutHover(null);
        return;
      }
      const title = getLocalizedCategoryTitle(
        cat,
        i18n.language,
        t('admin.categoryStats.noTitle')
      );
      const count = Number(cat.companiesCount) || 0;
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
      let left = e.clientX + TOOLTIP_PAD;
      let top = e.clientY + TOOLTIP_PAD;
      if (left + TOOLTIP_W > vw - 8) left = e.clientX - TOOLTIP_W - TOOLTIP_PAD;
      if (left < 8) left = 8;
      if (top + TOOLTIP_H > vh - 8) top = e.clientY - TOOLTIP_H - TOOLTIP_PAD;
      if (top < 8) top = 8;
      setDonutHover({
        left,
        top,
        title,
        count,
      });
    },
    [sliceRanges, pickSliceByPercent, i18n.language, t]
  );

  const clearDonutHover = useCallback(() => setDonutHover(null), []);

  const header = (
    <div className="card-header">
      <h3 className="card-title">
        <div className="card-icon-circle">
          <Icon name="pie-chart" size={14} />
        </div>
        {t('admin.categoryStats.shortTitle')}
      </h3>
      {!loading && !error && (
        <span className="pill pill-blue">
          {t('admin.categoryStats.total')}: {totalCompanies}
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-panel admin-category-stats-panel">
        {header}
        <div className="category-stats-loading">
          <div className="loading-spinner" />
          <p>{t('admin.categoryStats.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel admin-category-stats-panel">
        <div className="card-header">
          <h3 className="card-title">
            <div className="card-icon-circle">
              <Icon name="pie-chart" size={14} />
            </div>
            {t('admin.categoryStats.shortTitle')}
          </h3>
        </div>
        <div className="category-stats-error">
          <p className="error-message">❌ {error}</p>
          <button
            type="button"
            className="admin-btn ghost small"
            onClick={() => window.location.reload()}
          >
            {t('admin.categoryStats.refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel admin-category-stats-panel">
      {header}

      {stats.length === 0 ? (
        <div className="category-stats-empty">
          <p>{t('admin.categoryStats.noData')}</p>
        </div>
      ) : (
        <div className="category-stats-container">
          <div className="category-stats-chart-stack">
            <div
              ref={donutWrapRef}
              className="category-stats-donut-wrap category-stats-donut-wrap--interactive"
              onMouseMove={updateDonutHover}
              onMouseLeave={clearDonutHover}
            >
              {conicGradient ? (
                <div
                  className="category-stats-donut-ring"
                  style={{ background: conicGradient }}
                  role="img"
                  aria-label={t('admin.categoryStats.chartAria')}
                />
              ) : (
                <div className="category-stats-donut-ring category-stats-donut-ring--empty" />
              )}
              <div className="category-stats-donut-hole">
                <span className="category-stats-donut-total">{totalCompanies}</span>
                <span className="category-stats-donut-label">
                  {t('admin.categoryStats.companies')}
                </span>
              </div>
            </div>

            <div className="category-stats-full-list">
              <p className="category-stats-full-list-heading">
                {t('admin.categoryStats.allCategories')}
              </p>
              <ul className="category-stats-full-list-ul">
                {sortedByCount.map((category) => {
                  const title = getLocalizedCategoryTitle(
                    category,
                    i18n.language,
                    t('admin.categoryStats.noTitle')
                  );
                  const companiesCount = Number(category.companiesCount) || 0;
                  const idStr = String(category.categoryId ?? '');
                  const dotColor =
                    colorByCategoryId.get(idStr) || NO_SLICE_COLOR;
                  return (
                    <li key={idStr || title} className="category-stats-full-list-item">
                      <span
                        className="category-stats-full-list-dot"
                        style={{ background: dotColor }}
                        aria-hidden
                      />
                      <span className="category-stats-full-list-name">{title}</span>
                      <span className="category-stats-full-list-count">{companiesCount}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {donutHover &&
            createPortal(
              <div
                className="category-stats-donut-tooltip"
                style={{
                  position: 'fixed',
                  left: donutHover.left,
                  top: donutHover.top,
                  zIndex: 20000,
                }}
                role="tooltip"
              >
                <div className="category-stats-donut-tooltip-title">{donutHover.title}</div>
                <div className="category-stats-donut-tooltip-count">
                  {t('admin.categoryStats.donutShortCount', { count: donutHover.count })}
                </div>
              </div>,
              document.body
            )}
        </div>
      )}
    </div>
  );
};

export default CategoryStats;
